import React from 'react';
import { useAuth } from '../hooks/useAuth';

export interface LoginButtonProps {
  /**
   * Button text
   * @default 'Login'
   */
  children?: React.ReactNode;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Button variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'outline';

  /**
   * Button size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Show login icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Callback before login starts
   */
  onLoginStart?: () => void;

  /**
   * Custom inline styles
   */
  style?: React.CSSProperties;

  /**
   * Disable the button
   */
  disabled?: boolean;
}

/**
 * Pre-built login button component
 *
 * Triggers the Keycloak login flow when clicked. The button automatically
 * hides when the user is already authenticated.
 *
 * @example Basic usage
 * ```tsx
 * import { LoginButton } from 'antaeus.keycloak.react';
 *
 * function Header() {
 *   return <LoginButton />;
 * }
 * ```
 *
 * @example Customized
 * ```tsx
 * <LoginButton
 *   variant="outline"
 *   size="large"
 *   onLoginStart={() => console.log('Starting login...')}
 * >
 *   Sign In
 * </LoginButton>
 * ```
 *
 * @example With custom styling
 * ```tsx
 * <LoginButton
 *   className="my-custom-class"
 *   style={{ backgroundColor: '#007bff' }}
 * />
 * ```
 */
export const LoginButton: React.FC<LoginButtonProps> = ({
  children = 'Login',
  className = '',
  variant = 'primary',
  size = 'medium',
  showIcon = true,
  onLoginStart,
  style,
  disabled = false,
}) => {
  const auth = useAuth();

  // Don't render if already authenticated
  if (auth.isAuthenticated) {
    return null;
  }

  const handleClick = () => {
    onLoginStart?.();
    auth.signinRedirect();
  };

  const baseClass = 'kc-button kc-login-button';
  const variantClass = `kc-button--${variant}`;
  const sizeClass = `kc-button--${size}`;
  const classes = `${baseClass} ${variantClass} ${sizeClass} ${className}`.trim();

  return (
    <button
      type="button"
      className={classes}
      onClick={handleClick}
      disabled={disabled || auth.isLoading}
      style={style}
    >
      {showIcon && <span className="kc-button__icon">🔐</span>}
      <span className="kc-button__text">{children}</span>
    </button>
  );
};
